<template>
  <div class="container mt-lg-5 mt-5">
    <!-- Mobile filter toggle -->
    <button 
      class="filter-toggle-btn d-lg-none"
      @click="showMobileFilters = true"
    >
      <i class="fas fa-filter me-2"></i>
      Filtros
      <span v-if="activeFiltersCount" class="filter-count">{{ activeFiltersCount }}</span>
    </button>

    <div class="product-list-container pt-0">
      <!-- Sidebar with filters -->
      <div class="sidebar" :class="{ 'mobile-active': showMobileFilters }">
        <div class="filter-section">
          <h1 class="display-6 fw-bold text-gradient-primary pb-3">Catálogo de productos
          </h1>
          <!-- Mobile header -->
          <div class="mobile-filter-header d-lg-none">
            <h3>Filtros</h3>
            <button class="close-filters" @click="showMobileFilters = false">
              <i class="fas fa-times"></i>
            </button>
          </div>
          
          <!-- Search box with icon -->
          <div class="search-box">
            <i class="fas fa-search search-icon"></i>
            <input  
              type="text" 
              v-model="searchQuery"
              placeholder="Buscar por producto, marca o categoría..."
              class="search-input"
              @input="handleSearch"
            >
            <button 
              v-if="searchQuery"
              @click="clearSearch" 
              class="clear-search"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="categories-section">
            <div class="category-groups">
              <div 
                v-for="group in categoryGroups" 
                :key="group.main" 
                class="category-group"
                :class="{ 'active': isExpanded(group.main) }"
              >
                <div class="group-header" @click="toggleGroup(group.main)">
                  <div class="group-header-content">
                    <i :class="getCategoryIcon(group.main)" class="category-icon"></i>
                    <span>{{ group.main }}</span>
                  </div>
                  <i class="fas fa-chevron-down" :class="{ 'rotate': isExpanded(group.main) }"></i>
                </div>
                <div class="group-content" :class="{ show: isExpanded(group.main) }">
                  <button 
                    v-for="sub in group.sub" 
                    :key="sub.value"
                    class="subcategory-item"
                    :class="{ active: isSubcategoryActive(sub.value) }"
                    @click="selectSubcategory(sub)"
                  >
                    <span>{{ sub.label }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Mobile filter actions -->
        <div class="mobile-filter-actions d-lg-none">
          <button class="btn btn-outline-secondary" @click="clearAllFilters">
            Limpiar filtros
          </button>
          <button class="btn btn-primary" @click="applyFilters">
            Ver {{ filteredProducts.length }} resultados
          </button>
        </div>
      </div>

      <!-- Products grid con paginación -->
      <div class="products-section">
        <!-- Overlay de carga -->
        <div class="loading-overlay" v-if="isLoading && !initialLoad">
          <div class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
            <span>Cargando productos...</span>
          </div>
        </div>

        <div class="products-grid pt-0">
          <!-- Loading state - Usar key único para el skeleton -->
          <template v-if="isLoading && initialLoad">
            <ProductSkeleton 
              v-for="n in pageSize" 
              :key="`skeleton-${n}`"
              class="product-item s"
            />
          </template>

          <!-- No products state - Añadir key único -->
          <div v-else-if="!products.length" class="no-products" key="no-products">
            <div class="no-products-content">
              <i class="fas fa-search mb-3 display-1"></i>
              <h3 class="fw-semibold">No se encontraron productos</h3>
              <p class="text-muted mb-4">
                No hay productos que coincidan con los filtros seleccionados.
                Intenta cambiar los criterios de búsqueda.
              </p>
              <div>
                <button @click="clearAllFilters" class="btn btn-primary">
                  <i class="fas fa-times me-2"></i>
                  Limpiar filtros
                </button>
              </div>
            </div>
          </div>
          
          <!-- Products - Usar productos estables con key única -->
          <template v-else>
            <ProductCard
              v-for="product in products"
              :key="`product-${product.id}-${currentPage}`"
              :product="product"
              class="product-item"
            />
          </template>
        </div>
        
        <!-- Paginación -->
        <div v-if="totalPages > 1" class="pagination-container">
          <button 
            class="pagination-btn"
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          >
            <i class="fas fa-chevron-left"></i>
          </button>

          <button 
            v-for="page in displayedPages"
            :key="page"
            class="pagination-btn"
            :class="{ active: page === currentPage }"
            @click="changePage(page)"
          >
            {{ page }}
          </button>

          <button 
            class="pagination-btn"
            :disabled="currentPage === totalPages"
            @click="changePage(currentPage + 1)"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick, defineProps } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import ProductCard from './ProductCard.vue'
import ProductSkeleton from './ProductSkeleton.vue'
import { getProducts } from '@/services/products'

const props = defineProps({
  isProductsPage: {
    type: Boolean,
    default: true
  }
})

const router = useRouter()
const route = useRoute()

// Estado base
const products = ref([])
const isLoading = ref(true)
const initialLoad = ref(true)
const currentPage = ref(1)
const totalItems = ref(0)
const pageSize = ref(6)
const searchQuery = ref('')
const selectedCategories = ref([])
const showMobileFilters = ref(false)
const loadingError = ref(null)

// Nuevo estado para precargar productos
const preloadedProducts = ref({})
const isPreloading = ref(false)

// Paginación computed props
const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value))

const displayedPages = computed(() => {
  const range = 2
  const pages = []
  const start = Math.max(1, currentPage.value - range)
  const end = Math.min(totalPages.value, currentPage.value + range)

  if (start > 1) pages.push(1)
  if (start > 2) pages.push('...')
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  if (end < totalPages.value - 1) pages.push('...')
  if (end < totalPages.value) pages.push(totalPages.value)
  
  return pages
})

// Añadir el computed property filteredProducts
const filteredProducts = computed(() => {
  if (!products.value) return []
  
  let filtered = products.value

  // Filtrar por categoría
  if (selectedCategories.value.length > 0) {
    filtered = filtered.filter(product => 
      selectedCategories.value.includes(product.category_name)
    )
  }

  // Filtrar por búsqueda
  if (searchQuery.value.trim()) {
    const search = searchQuery.value.toLowerCase().trim()
    filtered = filtered.filter(product => {
      const searchableText = [
        product.name,
        product.brand,
        product.category_name,
        product.description
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      
      return searchableText.includes(search)
    })
  }

  // Filtrar productos no disponibles
  filtered = filtered.filter(product => product.is_available)

  return filtered
})

// Añadir computed para el conteo de filtros activos
const activeFiltersCount = computed(() => {
  let count = 0
  if (searchQuery.value) count++
  if (selectedCategories.value.length) count++
  return count
})

// Agregar estado para grupos expandidos
const expandedGroups = ref({})

// Definir los grupos de categorías
const categoryGroups = ref([
  {
    main: 'Cultivo',
    icon: 'fas fa-seedling',
    sub: [
      { value: 'Sustratos', label: 'Sustratos' },
      { value: 'Fertilizantes', label: 'Fertilizantes' },
      { value: 'Macetas', label: 'Macetas' },
      { value: 'Plaguicidas', label: 'Control de plagas' },
      { value: 'Otros', label: 'Otros' }
    ]
  },
  {
    main: 'Indoor',
    icon: 'fas fa-lightbulb',
    sub: [
      { value: 'Iluminación', label: 'Iluminación' },
      { value: 'Carpas', label: 'Carpas' },
      { value: 'Artículos indoor', label: 'Ventilación' },
      { value: 'Artículos indoor', label: 'Medidores' }
    ]
  },
  {
    main: 'Parafernalia',
    icon: 'fas fa-bong',
    sub: [
      { value: 'Pipas', label: 'Pipas' },
      { value: 'Bongs', label: 'Bongs' },
      { value: 'Sedas', label: 'Sedas' },
      { value: 'Picadores', label: 'Picadores' }
    ]
  },
  {
    main: 'Otros',
    icon: 'fas fa-cube',
    sub: [
      { value: 'Hongos', label: 'Hongos' },
      { value: 'Vaporizadores', label: 'Vaporizadores' }
    ]
  }
])

// Funciones para manejar categorías
const toggleGroup = (groupName) => {
  expandedGroups.value[groupName] = !expandedGroups.value[groupName]
}

const isExpanded = (groupName) => {
  return !!expandedGroups.value[groupName]
}

const getCategoryIcon = (category) => {
  const icons = {
    'Cultivo': 'fas fa-seedling',
    'Indoor': 'fas fa-lightbulb',
    'Parafernalia': 'fas fa-bong',
    'Otros': 'fas fa-cube'
  }
  return icons[category] || 'fas fa-folder'
}

const isSubcategoryActive = (subcategory) => {
  return selectedCategories.value.includes(subcategory)
}

const selectSubcategory = (sub) => {
  const index = selectedCategories.value.indexOf(sub.value)
  
  if (index === -1) {
    selectedCategories.value = [sub.value]
  } else {
    selectedCategories.value = []
  }
  
  showMobileFilters.value = false
  handleSearch()
}

// Métodos
// Mejorar el método de carga de productos
const loadProducts = async (page = 1, preloadNextPage = true) => {
  try {
    isLoading.value = true
    loadingError.value = null
    
    // Asegurarse de que la página sea un número válido
    page = Math.max(1, parseInt(page) || 1)
    
    // Verificar si tenemos productos precargados para esta página
    if (preloadedProducts.value[page]) {
      console.log('Usando productos precargados para página', page)
      products.value = [...preloadedProducts.value[page].results]
      totalItems.value = preloadedProducts.value[page].count || 0
      currentPage.value = page
      
      // Limpiamos los productos preload para esta página para no retener datos obsoletos
      delete preloadedProducts.value[page]
      
      isLoading.value = false
      
      // Precargamos la siguiente página después de un pequeño delay
      if (preloadNextPage && page < Math.ceil(totalItems.value / pageSize.value)) {
        setTimeout(() => preloadPageProducts(page + 1), 300)
      }
      
      return
    }
    
    const params = {
      page: page,
      page_size: pageSize.value
    }

    // Añadir parámetros opcionales solo si tienen valor
    if (searchQuery.value?.trim()) {
      params.search = searchQuery.value.trim()
    }

    if (selectedCategories.value.length) {
      params.category = selectedCategories.value[0]
    }

    console.log('Cargando productos con params:', params)
    
    // Esperar un poco para mostrar la animación de carga
    const response = await getProducts(params)
    
    // Verificar que la respuesta sea válida
    if (!response || !Array.isArray(response.results)) {
      console.error('Respuesta inválida del API:', response)
      throw new Error('Formato de respuesta inválido')
    }
    
    // Actualizar estado después de recibir respuesta correcta
    await nextTick()
    products.value = [...response.results]
    totalItems.value = response.count || 0
    currentPage.value = page
    
    // Precargar la siguiente página si existe
    if (preloadNextPage && page < Math.ceil(totalItems.value / pageSize.value)) {
      setTimeout(() => preloadPageProducts(page + 1), 300)
    }
    
  } catch (error) {
    console.error('Error al cargar productos:', error)
    loadingError.value = 'Error al cargar los productos. Por favor intenta nuevamente.'
    products.value = []
    totalItems.value = 0
  } finally {
    // Pequeño delay para suavizar la transición
    setTimeout(() => {
      isLoading.value = false
      initialLoad.value = false
    }, 300)
  }
}

// Nuevo método para precargar la siguiente página de productos
const preloadPageProducts = async (page) => {
  // No precargar si ya estamos precargando o si ya tenemos esa página
  if (isPreloading.value || preloadedProducts.value[page] || page > totalPages.value) {
    return
  }
  
  try {
    isPreloading.value = true
    
    const params = {
      page: page,
      page_size: pageSize.value
    }
    
    // Añadir los mismos filtros que se usan actualmente
    if (searchQuery.value?.trim()) {
      params.search = searchQuery.value.trim()
    }
    
    if (selectedCategories.value.length) {
      params.category = selectedCategories.value[0]
    }
    
    console.log('Precargando página', page, 'con params:', params)
    
    const response = await getProducts(params)
    
    // Guardar los productos preload si la respuesta es válida
    if (response && Array.isArray(response.results)) {
      preloadedProducts.value[page] = response
      console.log('Página', page, 'precargada con éxito')
    }
  } catch (error) {
    console.error('Error al precargar productos de la página', page, error)
    // No mostramos error al usuario ya que es preload
  } finally {
    isPreloading.value = false
  }
}

// Mejorar el método changePage
const changePage = async (page) => {
  if (page === '...' || page === currentPage.value) return
  
  try {
    // Actualizar la URL primero para reflejar el cambio de página
    await updateRouteWithFilters(page)
    
    // Cargar los productos de la nueva página
    await loadProducts(page)
  } catch (error) {
    console.error('Error al cambiar de página:', error)
  }
}

// Actualizar este método para aceptar el parámetro de página
const updateRouteWithFilters = async (page = currentPage.value) => {
  const query = {
    page: page,
  }
  
  if (searchQuery.value?.trim()) {
    query.search = searchQuery.value.trim()
  }
  
  if (selectedCategories.value.length) {
    query.categories = selectedCategories.value.join(',')
  }
  
  // Usar la navegación de router de forma más segura
  await router.push({ query }).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      throw err
    }
  })
}

// Mejorar manejo de búsqueda
const handleSearch = () => {
  // Limpiar productos precargados cuando cambian los filtros
  preloadedProducts.value = {}
  
  currentPage.value = 1
  updateRouteWithFilters(1)
  loadProducts(1)
}

const clearSearch = () => {
  searchQuery.value = ''
  handleSearch()
}

// Método para limpiar todos los filtros - Conservamos solo esta implementación
const clearAllFilters = () => {
  // Limpiar productos precargados cuando se limpian los filtros
  preloadedProducts.value = {}
  
  searchQuery.value = ''
  selectedCategories.value = []
  currentPage.value = 1
  showMobileFilters.value = false // Cerrar el menú móvil al limpiar
  
  // Remove filters-active class from body
  document.body.classList.remove('filters-active');
  
  // Actualizar la URL y cargar productos
  updateRouteWithFilters(1)
  loadProducts(1)
}

const applyFilters = () => {
  // Limpiar productos precargados cuando se aplican los filtros
  preloadedProducts.value = {}
  
  showMobileFilters.value = false
  
  // Remove filters-active class from body
  document.body.classList.remove('filters-active');
  
  loadProducts(currentPage.value)
}

// Watchers
// Mejorar el watch de la ruta para manejar mejor los cambios
watch(() => route.query, (newQuery, oldQuery) => {
  // Evitar recargas innecesarias si no hay cambios reales
  if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return
  
  const newPage = parseInt(newQuery.page) || 1
  const newSearch = newQuery.search || ''
  const newCategories = newQuery.categories ? newQuery.categories.split(',') : []
  
  // Solo actualizar valores si han cambiado para evitar loops infinitos
  let needsReload = false
  
  if (searchQuery.value !== newSearch) {
    searchQuery.value = newSearch
    // Limpiar productos precargados cuando cambia la búsqueda
    preloadedProducts.value = {}
    needsReload = true
  }
  
  if (JSON.stringify(selectedCategories.value) !== JSON.stringify(newCategories)) {
    selectedCategories.value = [...newCategories]
    // Limpiar productos precargados cuando cambian las categorías
    preloadedProducts.value = {}
    needsReload = true
  }
  
  if (currentPage.value !== newPage) {
    currentPage.value = newPage
    needsReload = true
  }
  
  // Solo recargar si algo cambió realmente
  if (needsReload) {
    loadProducts(currentPage.value)
  }
}, { immediate: true, deep: true })

onMounted(() => {
  initialLoad.value = true
  const page = parseInt(route.query.page) || 1
  loadProducts(page)
})

// Modificar pageSize según la página donde se use el componente
pageSize.value = props.isProductsPage ? 6 : 6;
</script>

<style scoped>
.product-list-container {
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 2rem;
  padding-top: 100px;
  transition: all 0.3s ease;
}

.sidebar {
  position: sticky;
  top: 10px;
  height: fit-content;
  margin-top: 0;
}

.filter-section {
  background: var(--black-color-2);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 0;
  border: 1px solid var(--border-color);
}

.filter-section h3 {
  color: var(--white-color);
  margin-bottom: 1rem;
  font-size: 1rem;
}

.category-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
select{
  background-color: #000;
  color: #fff;
  padding: 15px !important;
}
.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.75rem 1rem;
  background: var(--black-color-3);
  border: 1px solid var(--border-color);
  color: var(--white-color-2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.category-count {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.category-item:hover {
  background: var(--primary-color);
  color: var(--black-color);
}

.category-item.active {
  background: var(--primary-color);
  color: var (--black-color-2);
  font-weight: 500;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 2rem;
  padding: 1rem 0;
  min-height: 400px;
}

.product-item {
  transition: opacity 0.3s ease;
}

/* Ajustes responsive para el grid de productos */
@media (max-width: 768px) {
  .products-grid {
    grid-template-columns: repeat(2, 1fr); /* 2 productos por fila */
    gap: 1rem;
  }
}

@media (max-width: 400px) {
  .products-grid {
    grid-template-columns: 1fr; /* 1 producto por fila */
    gap: 1rem;
  }
}

.no-products {
  grid-column: 1 / -1;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  background: var (--black-color-2);
  border: 1px solid var(--border-color);
  border-radius: 12px;
}

.no-products-content {
  max-width: 400px;
}


.no-products h3 {
  color: var(--white-color);
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.no-products p {
  color: var(--text-muted);
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .no-products {
    min-height: 300px;
    padding: 1.5rem;
  }

  .no-products h3 {
    font-size: 1.25rem;
  }

  .no-products p {
    font-size: 0.9rem;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .product-list-container {
    grid-template-columns: 1fr;
    padding: 0;
  }

  .sidebar {
    position: relative;
    top: 0;
  }

  .category-list {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .category-item {
    flex: 1;
    min-width: 150px;
    text-align: center;
  }
}

.search-box {
  position: relative;
  margin-bottom: 1.5rem;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--black-color-3);
  color: var(--white-color);
  font-size: 0.9rem;
}

.clear-search {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
}

.active-filters {
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: var(--black-color-3);
  border-radius: 12px;
  border: 1px solid var(--border-color);
}

.active-filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.active-filters-title {
  color: var(--white-color);
  font-size: 0.9rem;
  font-weight: 500;
}

.filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  background: var(--black-color-2);
  border: 1px solid var(--border-color);
  color: var(--white-color);
  border-radius: 20px;
  position: relative;
  flex: 1;
}

.price-symbol {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
}

.price-input input {
  width: 100%;
  padding: 0.5rem 0.5rem 0.5rem 1.75rem;
  background: var(--black-color-3);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--white-color);
  font-size: 0.9rem;
}

.price-separator {
  color: var(--text-muted);
}

.price-slider {
  position: relative;
  height: 4px;
  background: var(--border-color);
  border-radius: 2px;
  margin: 2rem 0 1rem;
}

.price-slider input[type="range"] {
  position: absolute;
  width: 100%;
  height: 100%;
  background: none;
  pointer-events: none;
  -webkit-appearance: none;
}

.price-slider input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 16px;
  width: 16px;
  border-radius: 50%;
  background: var(--primary-color);
  cursor: pointer;
  pointer-events: auto;
  margin-top: -6px;
}

.price-range-progress {
  position: absolute;
  height: 100%;
  background: var(--primary-color);
  border-radius: 2px;
  pointer-events: none;
}

.products-header {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--black-color-2);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.products-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.products-count {
  color: var(--text-muted);
  font-size: 0.9rem;
  margin: 0;
}

.sort-wrapper {
  min-width: 200px;
}

.sort-select {
  width: 100%;
  padding: 0.5rem 1rem;
  background: var(--black-color-3);
  border: 1px solid var(--border-color);
  color: var(--white-color);
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
}

.sort-select:focus {
  border-color: var(--primary-color);
  outline: none;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .products-header-content {
    flex-direction: column;
    gap: 1rem;
  }

  .sort-wrapper {
    width: 100%;
  }
}

.load-more-container {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
  text-align: center;
}

.load-more-button {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.load-more-button:hover {
  background: var(--primary-color-dark);
  transform: translateY(-1px);
}

/* Mobile Styles */
.filter-toggle-btn {
  width: 100%;
  padding: 1rem;
  margin-bottom: 1rem;
  background: var(--black-color-2);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--white-color);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.filter-count {
  background: var(--primary-color);
  color: var(--black-color);
  padding: 0.2rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.8rem;
}

@media (max-width: 991.98px) {
  .sidebar {
    display: none; /* Ocultar por defecto */
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--black-color);
    z-index: 9999; /* Increased z-index to be above everything */
    padding: 0;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .sidebar.mobile-active {
    display: block; /* Mostrar cuando está activo */
    transform: translateY(0);
    z-index: 1000;
  }

  .filter-section {
    height: calc(100vh - 60px);
    overflow-y: auto;
    padding: 1rem;
    padding-bottom: 120px;
    border-radius: 20px 20px 0 0;
    border-bottom: none;
    margin-top: 0;
    padding-top: 80px; /* Add padding-top to filter-section to account for fixed header */
  }

  .mobile-filter-header {
    position: fixed; /* Change to fixed position */
    top: 0;
    left: 0;
    right: 0;
    background: var(--black-color-2);
    padding: 1rem;
    border-radius: 20px 20px 0 0;
    z-index: 10000; /* Higher z-index than sidebar */
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--border-color);
  }

  .close-filters {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--black-color-3);
    border: 1px solid var(--border-color);
    color: var(--white-color);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mobile-filter-actions {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1rem;
    background: var(--black-color-2);
    display: flex;
    gap: 1rem;
    border-top: 1px solid var(--border-color);
    z-index: 10000; /* Higher z-index than sidebar */
  }

  .mobile-filter-actions button {
    flex: 1;
    padding: 1rem;
    border-radius: 12px;
  }

  /* Mejorar el botón de toggle de filtros */
  .filter-toggle-btn {
    background: var(--black-color-2);
    border: 1px solid var(--border-color);
    color: var(--white-color);
    padding: 0.75rem 1rem;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 1rem;
    transition: all 0.2s ease;
  }

  .filter-toggle-btn:active {
    transform: scale(0.98);
  }

  .filter-count {
    background: var(--primary-color);
    color: var(--black-color);
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 500;
  }

  /* Ajustes para categorías en mobile */
  .category-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .category-item {
    padding: 1rem;
    border-radius: 12px;
    text-align: left;
  }

  /* Ajustes para el range de precios */
  .price-slider input[type="range"]::-webkit-slider-thumb {
    width: 24px;
    height: 24px;
    margin-top: -10px;
  }
}

/* Add style to prevent body scroll when filters are active */
body.filters-active {
  overflow: hidden !important;
  position: fixed;
  width: 100%;
  height: 100%;
}

.category-group {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  margin-bottom: 0.5rem;
  overflow: hidden;
}

.group-header {
  width: 100%;
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--black-color-3);
  border: none;
  color: var(--white-color);
  cursor: pointer;
  transition: all 0.2s ease;
}

.group-header:hover {
  background: var(--black-color-2);
}

.group-header i {
  transition: transform 0.2s ease;
}

.group-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
}

.group-content.show {
  max-height: 500px;
}



/* Responsive adjustments */
@media (max-width: 768px) {
  .category-group {
    width: 100%;
  }
  
  .subcategory-item {
    width: 100%;
    text-align: left;
  }
}
.group-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
}

.group-content.show {
  max-height: 500px; /* Asegúrate de que este valor sea suficiente para tu contenido */
}

.subcategory-item {
  width: 100%;
  padding: 0.5rem 1.5rem;
  background: var(--black-color-2);
  border: none;
  color: var(--white-color-2);
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
}

.subcategory-item:hover,
.subcategory-item.active {
  background: var(--primary-color);
  color: var(--black-color);
  padding-left: 2rem;
}

.subcategory-count {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.subcategory-item:hover .subcategory-count,
.subcategory-item.active .subcategory-count {
  color: inherit;
}

.category-group {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;
}

.category-group.active {
  border-color: var(--primary-color);
}

.group-header-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.category-icon {
  color: var(--primary-color);
  width: 20px;
}

.category-group {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  margin-bottom: 0.75rem;
  overflow: hidden;
  transition: all 0.2s ease;
}

.group-header {
  padding: .7rem 1.25rem;
  background: var(--black-color-3);
}

.group-header-content i {
  font-size: 1.1rem;
  width: 24px;
  text-align: center;
}

.subcategory-item {
  padding: 0.6rem 1.25rem 0.6rem 3.5rem;
  position: relative;
}

.subcategory-item::before {
  content: '';
  position: absolute;
  left: 2.5rem;
  top: 50%;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--primary-color);
  transform: translateY(-50%);
  opacity: 0;
  transition: all 0.2s ease;
}

.subcategory-item:hover::before,
.subcategory-item.active::before {
  opacity: 1;
  left: 1.75rem;
}

.pagination-container {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
  padding: 1rem;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  background: var(--black-color-2);
  color: var(--white-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--primary-color);
  color: var(--black-color);
}

.pagination-btn.active {
  background: var(--primary-color);
  color: var(--black-color);
  border-color: var(--primary-color);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .pagination-btn {
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;
  }
}

.group-header i.fa-chevron-down {
  transition: transform 0.3s ease;
}

.group-header i.fa-chevron-down.rotate {
  transform: rotate(-180deg);
}

.category-icon {
  width: 20px;
  text-align: center;
  color: var(--primary-color);
}

/* Animación de carga */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  background: var(--black-color-2);
  padding: 1.5rem 2rem;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.loading-spinner i {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  animation: spinPulse 1.5s infinite ease;
}

.loading-spinner span {
  font-size: 0.9rem;
  color: var(--white-color);
}

@keyframes spinPulse {
  0% {
    transform: scale(0.95) rotate(0deg);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1) rotate(180deg);
    opacity: 1;
  }
  100% {
    transform: scale(0.95) rotate(360deg);
    opacity: 0.8;
  }
}

/* Ajuste para la section de productos */
.products-section {
  position: relative;
}

/* Mejorar la animación de carga para hacerla más suave */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Añadir animación más suave para los productos */
.product-item {
  animation: fadeInUp 0.4s ease;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Mejorar la animación del spinner para hacerla más suave */
@keyframes spinPulse {
  0% {
    transform: scale(0.95) rotate(0deg);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1) rotate(180deg);
    opacity: 1;
  }
  100% {
    transform: scale(0.95) rotate(360deg);
    opacity: 0.8;
  }
}

@media (max-width: 991.98px) {
  .product-list-container {
    padding-top: 50px; /* Reducir padding en móvil */
  }
}
</style>
